import { floorToTwoDecimalPlaces } from '@/lib/number';
import { Cart } from '@/schema/cart';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface CartState {
  serials: string[];
  carts: Cart[];
  totalWeight: number;
  totalQuantity: number;
  totalSalesCounting: number;
  originalTotalProductPrice: number;
  originalTotalShippingPrice: number;
  currency: 'KRW' | 'USD' | 'JPY';
  deliveryUserCoupon?: ICoupon;
  deliveryUserCouponDiscountAmount: number;
  purpleEnergy: number;
  purpleEnergyDiscountAmount: number;
  redirectUrl?: string;
  finalTotalProductCouponDiscountAmount: number;
  paymentMethod: 'Card' | 'EasyPay';
  orderCoupon?: ICoupon;
  orderCouponDiscountAmount: number;
}

const initialCartState: CartState = {
  serials: [],
  carts: [],
  totalWeight: 0.0,
  totalQuantity: 0,
  totalSalesCounting: 0,
  originalTotalProductPrice: 0,
  originalTotalShippingPrice: 0,
  currency: 'KRW',
  deliveryUserCouponDiscountAmount: 0,
  purpleEnergy: 0,
  purpleEnergyDiscountAmount: 0,
  finalTotalProductCouponDiscountAmount: 0,
  paymentMethod: 'Card',
  orderCouponDiscountAmount: 0,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState: initialCartState,
  reducers: {
    setCartSerials: (state, action: PayloadAction<string[]>) => {
      state.serials = action.payload;
    },
    setCarts: (state, action: PayloadAction<Cart[]>) => {
      state.carts = action.payload;

      const totalInfo = action.payload.reduce(
        (acc, cur) => {
          return {
            totalWeight: Number(
              (acc.totalWeight + cur.cart.totalWeight).toFixed(2),
            ),
            totalPrice: Number(
              (acc.totalPrice + cur.cart.totalPrice).toFixed(2),
            ),
            totalQuantity: acc.totalQuantity + cur.cart.totalQuantity,
            totalSalesCounting:
              acc.totalSalesCounting + cur.cart.totalSalesCounting,
          };
        },
        {
          totalWeight: 0,
          totalPrice: 0,
          totalQuantity: 0,
          totalSalesCounting: 0,
        },
      );
      state.totalWeight = totalInfo.totalWeight;
      state.totalQuantity = totalInfo.totalQuantity;
      state.totalSalesCounting = totalInfo.totalSalesCounting;
      state.originalTotalProductPrice = totalInfo.totalPrice;
      state.originalTotalShippingPrice = 0;
      state.deliveryUserCoupon = undefined;
      state.deliveryUserCouponDiscountAmount = 0;
      state.purpleEnergy = 0;
      state.purpleEnergyDiscountAmount = 0;
      state.finalTotalProductCouponDiscountAmount = 0;
      state.paymentMethod = 'Card';
      state.orderCoupon = undefined;
      state.orderCouponDiscountAmount = 0;
    },
    setCartRedirectUrl: (state, action: PayloadAction<string>) => {
      state.redirectUrl = action.payload;
    },
    setCartCurrency: (state, action: PayloadAction<'KRW' | 'USD' | 'JPY'>) => {
      state.currency = action.payload;
    },
    applyCouponToCart: (
      state,
      action: PayloadAction<{ shopId: string; coupon: ICoupon }>,
    ) => {
      const { shopId, coupon } = action.payload;
      const currencyKey = state.currency.toLowerCase() as 'krw' | 'usd' | 'jpy';

      // 새 상태 생성 (불변성 유지)
      state.carts = state.carts.map(cart => {
        if (cart.shopId !== shopId) return cart;

        const rawValue = coupon.coupon.discountValue[currencyKey] ?? 0;

        // 할인 금액 계산
        let discountAmount =
          coupon.coupon.discountType === 'fixed'
            ? rawValue
            : floorToTwoDecimalPlaces((cart.cart.totalPrice * rawValue) / 100);

        // ✅ 현재 이 cart에 이미 적용된 쿠폰이 있다면
        const prevDiscount = cart.productUserCouponDiscountAmount ?? 0;

        // ✅ 같은 쿠폰이더라도, 다시 반영하는 것이므로 기존 할인은 빼고 새로 더해야 함
        state.finalTotalProductCouponDiscountAmount -= prevDiscount;

        // ✅ 할인 총합이 원래 총 상품 금액을 초과하지 않도록 제한
        discountAmount = Number(
          Math.min(
            discountAmount,
            state.originalTotalProductPrice -
              state.finalTotalProductCouponDiscountAmount,
          ).toFixed(2),
        );

        // ✅ 최종 금액 다시 더함
        state.finalTotalProductCouponDiscountAmount += discountAmount;

        return {
          ...cart,
          productUserCouponId: coupon.id,
          productUserCouponDiscountAmount: discountAmount,
        };
      });

      // 최종 금액 변경으로 인한 주문 쿠폰 유지/해제
      const currentPurchaseAmount = calculateCurrentUserPurchaseAmount(
        state.originalTotalProductPrice,
        state.originalTotalShippingPrice,
        state.deliveryUserCouponDiscountAmount,
        state.finalTotalProductCouponDiscountAmount,
      );

      const { finalCoupon, finalDiscountAmount } = reCalculateOrderCoupon(
        currentPurchaseAmount,
        state.orderCoupon,
        currencyKey,
      );

      state.orderCoupon = finalCoupon;
      state.orderCouponDiscountAmount = finalDiscountAmount;
    },
    removeCouponFromCart: (
      state,
      action: PayloadAction<{ shopId: string }>,
    ) => {
      const { shopId } = action.payload;

      state.carts = state.carts.map(cart => {
        if (cart.shopId !== shopId) return cart;

        return {
          ...cart,
          productUserCouponId: undefined,
          productUserCouponDiscountAmount: 0,
        };
      });

      state.finalTotalProductCouponDiscountAmount = 0;

      // 최종 금액 변경으로 인한 주문 쿠폰 유지/해제
      const currencyKey = state.currency.toLowerCase() as 'krw' | 'usd' | 'jpy';

      const currentPurchaseAmount = calculateCurrentUserPurchaseAmount(
        state.originalTotalProductPrice,
        state.originalTotalShippingPrice,
        state.deliveryUserCouponDiscountAmount,
        state.finalTotalProductCouponDiscountAmount,
      );

      const { finalCoupon, finalDiscountAmount } = reCalculateOrderCoupon(
        currentPurchaseAmount,
        state.orderCoupon,
        currencyKey,
      );

      state.orderCoupon = finalCoupon;
      state.orderCouponDiscountAmount = finalDiscountAmount;
    },
    applyOrderCoupon: (state, action: PayloadAction<ICoupon>) => {
      const coupon = action.payload;
      const currencyKey = state.currency.toLowerCase() as 'krw' | 'usd' | 'jpy';

      const currentPurchaseAmount = calculateCurrentUserPurchaseAmount(
        state.originalTotalProductPrice,
        state.originalTotalShippingPrice,
        state.deliveryUserCouponDiscountAmount,
        state.finalTotalProductCouponDiscountAmount,
      );

      const { finalCoupon, finalDiscountAmount } = reCalculateOrderCoupon(
        currentPurchaseAmount,
        coupon,
        currencyKey,
      );

      state.orderCoupon = finalCoupon;
      state.orderCouponDiscountAmount = finalDiscountAmount;
    },
    resetOrderCoupon: state => {
      state.orderCoupon = undefined;
      state.orderCouponDiscountAmount = 0;
    },
    applyDeliveryCoupon: (state, action: PayloadAction<ICoupon>) => {
      const coupon = action.payload;
      const currencyKey = state.currency.toLowerCase() as 'krw' | 'usd' | 'jpy';

      const discountAmount = calculateDeliveryCouponDiscount(
        coupon,
        state.originalTotalShippingPrice,
        currencyKey,
      );

      state.deliveryUserCoupon = coupon;
      state.deliveryUserCouponDiscountAmount = discountAmount;

      // 최종 금액 변경으로 인한 주문 쿠폰 유지/해제
      const currentPurchaseAmount = calculateCurrentUserPurchaseAmount(
        state.originalTotalProductPrice,
        state.originalTotalShippingPrice,
        state.deliveryUserCouponDiscountAmount,
        state.finalTotalProductCouponDiscountAmount,
      );

      const { finalCoupon, finalDiscountAmount } = reCalculateOrderCoupon(
        currentPurchaseAmount,
        state.orderCoupon,
        currencyKey,
      );

      state.orderCoupon = finalCoupon;
      state.orderCouponDiscountAmount = finalDiscountAmount;
    },
    resetDeliveryCoupon: state => {
      state.deliveryUserCoupon = undefined;
      state.deliveryUserCouponDiscountAmount = 0;

      // 최종 금액 변경으로 인한 주문 쿠폰 유지/해제
      const currencyKey = state.currency.toLowerCase() as 'krw' | 'usd' | 'jpy';

      const currentPurchaseAmount = calculateCurrentUserPurchaseAmount(
        state.originalTotalProductPrice,
        state.originalTotalShippingPrice,
        state.deliveryUserCouponDiscountAmount,
        state.finalTotalProductCouponDiscountAmount,
      );

      const { finalCoupon, finalDiscountAmount } = reCalculateOrderCoupon(
        currentPurchaseAmount,
        state.orderCoupon,
        currencyKey,
      );

      state.orderCoupon = finalCoupon;
      state.orderCouponDiscountAmount = finalDiscountAmount;
    },
    setCartShippingFee: (state, action: PayloadAction<number>) => {
      state.originalTotalShippingPrice = action.payload;
      if (state.deliveryUserCoupon) {
        const currencyKey = state.currency.toLowerCase() as
          | 'krw'
          | 'usd'
          | 'jpy';
        state.deliveryUserCouponDiscountAmount =
          calculateDeliveryCouponDiscount(
            state.deliveryUserCoupon,
            action.payload,
            currencyKey,
          );
      }
    },
    setPurpleEnergyInfo: (
      state,
      action: PayloadAction<{
        purpleEnergy: number;
        convertedPurpleEnergy: number;
      }>,
    ) => {
      state.purpleEnergy = action.payload.purpleEnergy;
      state.purpleEnergyDiscountAmount = action.payload.convertedPurpleEnergy;
    },
    resetPurpleEnergyInfo: (state, action: PayloadAction<void>) => {
      state.purpleEnergy = 0;
      state.purpleEnergyDiscountAmount = 0;
    },
    setCartPaymentMethod: (
      state,
      action: PayloadAction<'Card' | 'EasyPay'>,
    ) => {
      state.paymentMethod = action.payload;
    },
  },
});

export const {
  setCartSerials,
  setCarts,
  setCartRedirectUrl,
  setCartCurrency,
  applyCouponToCart,
  removeCouponFromCart,
  applyOrderCoupon,
  resetOrderCoupon,
  applyDeliveryCoupon,
  resetDeliveryCoupon,
  setCartShippingFee,
  setPurpleEnergyInfo,
  resetPurpleEnergyInfo,
  setCartPaymentMethod,
} = cartSlice.actions;

export default cartSlice.reducer;

function calculateDeliveryCouponDiscount(
  coupon: ICoupon,
  originalShippingPrice: number,
  currencyKey: 'krw' | 'usd' | 'jpy',
): number {
  const rawValue = coupon.coupon.discountValue[currencyKey] ?? 0;

  const discountAmount =
    coupon.coupon.discountType === 'fixed'
      ? rawValue
      : floorToTwoDecimalPlaces((originalShippingPrice * rawValue) / 100);

  return Math.min(discountAmount, originalShippingPrice);
}

function calculateCurrentUserPurchaseAmount(
  originalTotalProductPrice: number,
  originalTotalShippingPrice: number,
  deliveryUserCouponDiscountAmount: number,
  finalTotalProductCouponDiscountAmount: number,
): number {
  return Number(
    (
      originalTotalProductPrice +
      originalTotalShippingPrice -
      deliveryUserCouponDiscountAmount -
      finalTotalProductCouponDiscountAmount
    ).toFixed(2),
  );
}

function reCalculateOrderCoupon(
  currentPurchaseAmount: number,
  orderCoupon: undefined | ICoupon,
  currencyKey: 'krw' | 'usd' | 'jpy',
) {
  let finalCoupon = undefined;
  let finalDiscountAmount = 0;

  if (orderCoupon) {
    const minPurchase = orderCoupon.coupon.minPurchase?.[currencyKey];
    if (!minPurchase || minPurchase <= currentPurchaseAmount) {
      finalCoupon = orderCoupon;
      const discountAmount = calculateDeliveryCouponDiscount(
        orderCoupon,
        currentPurchaseAmount,
        currencyKey,
      );

      const maxDiscount = orderCoupon.coupon.maxDiscount?.[currencyKey];
      finalDiscountAmount = maxDiscount
        ? Math.min(maxDiscount, discountAmount)
        : discountAmount;
    }
  }

  return { finalCoupon, finalDiscountAmount };
}
